// /onboarding/shape — shape 5, «ما برامجك؟» (board 25 v2 shape--5): the
// programs, each read back with its sessions. Row: «{name}», «{price} د.ع /
// مقعد · {seats} مقعداً» (course: «/ الدورة» and «{n} جلسات · تُحجز
// كاملة»), «يتكرّر أسبوعياً · {days} {time}» or «جلسة واحدة · {date}
// {time}{ · meeting point}». No hours for this shape (٥/٥). Finish needs one
// program with at least one session (R8).

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Plus } from 'lucide-react';
import { PrimaryAction } from '@/components/Button';
import { Skeleton } from '@/components/Skeleton';
import { toArabicDigits } from '@/lib/digits';
import { iqd } from '@/lib/format';
import { useLayout } from '@/lib/layout';
import { countLabel } from '@/lib/terminology';
import { Msg } from '../../components/widgets';
import { Wizard } from '../../components/Wizard';
import { PROGRAMS, SHAPE_COMMON, SHAPE_COPY } from '../../copy';
import { finishShape } from '../../data/enroll';
import { inBaghdad } from '../../data/sessionTime';
import { listPrograms, listSessions, type Program, type Session } from '../../data/shapeApi';
import { arTime } from '../../preview';
import { WEEK } from '../../state';
import { useBusinessPreview } from '../Business';
import { ProgramPanel } from './ProgramPanel';

const MONTHS = ['كانون الثاني', 'شباط', 'آذار', 'نيسان', 'أيار', 'حزيران', 'تموز', 'آب', 'أيلول', 'تشرين الأول', 'تشرين الثاني', 'كانون الأول'];

/** The three lines of a program row, from the program and its sessions. */
export function programRow(p: Program, sessions: Session[]): { price: string; schedule: string } {
  const active = sessions.filter((s) => s.status === 'active');
  const first = active[0];
  const seats = first ? PROGRAMS.seatsCount(toArabicDigits(first.seats)) : '';
  if (p.booking_kind === 'course') {
    // the program's own count (every upcoming session, not only the fetched window)
    return {
      price: `${iqd(p.seat_price_iqd)} / ${PROGRAMS.perCourse}`,
      schedule: `${countLabel(p.upcoming_sessions, PROGRAMS.sessionsWords)} · ${PROGRAMS.wholeCourse}`,
    };
  }
  const price = `${iqd(p.seat_price_iqd)} / ${PROGRAMS.perSeat}${seats ? ` · ${seats}` : ''}`;
  if (!first) {
    if (!p.next_session_at) return { price, schedule: '' };
    const next = inBaghdad(p.next_session_at);
    return { price, schedule: `${PROGRAMS.once} · ${toArabicDigits(next.day)} ${MONTHS[next.month]} ${arTime(next.hhmm)}${p.meeting_point ? ` · ${p.meeting_point}` : ''}` };
  }
  const at = inBaghdad(first.starts_at);
  const weekly = active.some((s) => s.series_id);
  if (weekly) {
    const days = new Set(active.filter((s) => s.series_id).map((s) => inBaghdad(s.starts_at).weekday));
    const names = WEEK.filter((d) => days.has(d.weekday)).map((d) => d.name).join('، ');
    return { price, schedule: `${PROGRAMS.weekly} · ${names} ${arTime(at.hhmm)}` };
  }
  const date = `${toArabicDigits(at.day)} ${MONTHS[at.month]}`;
  return { price, schedule: `${PROGRAMS.once} · ${date} ${arTime(at.hhmm)}${p.meeting_point ? ` · ${p.meeting_point}` : ''}` };
}

export function ProgramsStep({ businessId }: { businessId: string }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const mobile = useLayout() === 'phone';
  const key = ['onboarding', 'programs', businessId];
  const programs = useQuery({ queryKey: key, queryFn: () => listPrograms(businessId), staleTime: Infinity, retry: 0 });
  const list = programs.data ?? [];
  const sessions = useQueries({
    queries: list.map((p) => ({ queryKey: ['onboarding', 'sessions', businessId, p.id], queryFn: () => listSessions(businessId, p.id), staleTime: Infinity, retry: 0 })),
  });
  const [open, setOpen] = useState(false);

  const sessionsOf = (i: number) => sessions[i]?.data ?? [];
  const withSessions = list.map((p, i) => ({ p, s: sessionsOf(i) }));
  // R8 gates on the program's own upcoming_sessions (booking counts every active session after now)
  const ready = withSessions.filter((x) => x.p.active && x.p.upcoming_sessions > 0);
  const { profile, preview } = useBusinessPreview('shape');
  const canFinish = ready.length > 0;
  const loading = programs.isPending;

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: key });
    void qc.invalidateQueries({ queryKey: ['onboarding', 'sessions', businessId] });
  };

  return (
    <Wizard
      screen="shape"
      testId={loading ? 'onb-shape-loading' : 'onb-shape-programs'}
      state={loading ? 'loading' : 'default'}
      list
      question={SHAPE_COPY.seat.question()}
      sub={SHAPE_COPY.seat.sub()}
      primary={
        // while the panel is open its «حفظ» is the screen's one coral
        open ? undefined : (
          <PrimaryAction type="submit" size="lg" icon={ArrowLeft} block={mobile} disabled={!canFinish}>
            {SHAPE_COMMON.finish}
          </PrimaryAction>
        )
      }
      onSubmit={() => {
        if (canFinish && !open) finishShape(navigate);
      }}
      error={programs.isError ? { kind: 'network', onRetry: () => void programs.refetch() } : null}
      panel={{ phase: 'preview', profile, preview }}
    >
      {loading ? (
        <Skeleton height={120} />
      ) : (
        <>
          <div className="onb-listcard" data-testid="onb-programs">
            {withSessions.map(({ p, s }) => {
              const row = programRow(p, s);
              return (
                <div key={p.id} className="onb-row" data-testid="onb-pg-row">
                  <div className="onb-row-text">
                    <div className="onb-row-title">{p.name}</div>
                    <div className="onb-row-sub">{row.price}</div>
                    {row.schedule && <div className="onb-row-sub">{row.schedule}</div>}
                  </div>
                </div>
              );
            })}
            <button type="button" className="onb-entity-add" onClick={() => setOpen(true)}>
              <Plus aria-hidden="true" />
              {PROGRAMS.add}
            </button>
          </div>
          {!canFinish && (
            <Msg tone="info" testId="onb-shape-min-one">
              {SHAPE_COMMON.atLeastOne}
            </Msg>
          )}
        </>
      )}
      {open && <ProgramPanel businessId={businessId} onClose={() => setOpen(false)} onSaved={refresh} />}
    </Wizard>
  );
}

